// Der Abschnitt "Kontaktwege" des Kontaktdetails -- aus kontakt.tsx
// herausgeloest (Party 360, erster Schnitt; Verhalten unveraendert).

import { Fragment, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Subtitle1,
  Tooltip,
} from '@fluentui/react-components';
import {
  usageLabel,
  verificationLabel,
  bestaetigungPilleSichtbar,
  additionalContactPointNotice,
  withheldChannelNotice,
  type ContactPointView,
  type PartyDetail,
} from '../../parties.ts';
import { groupContactPoints, mailtoAdresse, telAdresse, websiteHref, type ContactPointGroup } from '../../contact-points.ts';
import { SperrHinweis, type SperrLage, type SperrenFuer } from './sperren-abschnitt.tsx';
import { NeuerTabZeichen, Symbol } from '../../shell/symbole.tsx';
import { textVorNeuerTab } from '../../neuer-tab.ts';
import type { ViaOrganization, ViaOrganizationValue } from '../../via-organization.ts';
import { useKontaktStyles } from './stile.ts';
import { HerkunftCaption, UebernommenerFeldname } from './ueber-organisation.tsx';

/**
 * Eine E-Mail bricht schmal hinter dem @ und vor den Punkten der Domain um, nicht
 * mitten im Wort; overflowWrap anywhere bleibt der Rückfall für sehr lange Teile.
 */
export function mitUmbruchstellen(wert: string, email: boolean): ReactNode {
  const at = wert.lastIndexOf('@');
  if (!email || at < 0) return wert;
  const [erster = '', ...rest] = wert.slice(at + 1).split('.');
  return (
    <>
      {wert.slice(0, at + 1)}
      <wbr />
      {erster}
      {rest.map((teil, index) => (
        <Fragment key={index}>
          <wbr />.{teil}
        </Fragment>
      ))}
    </>
  );
}

type Kopieren = (wert: string, was: string) => void;

/**
 * Ein Weg wird nur als mailto/tel-Link angeboten, wenn die Sperren geladen sind
 * und keine ihn trifft: ein Klick löste sonst genau das aus, wovor der Hinweis
 * warnt (fail-closed, auch bei "Sperren unvollständig" und während des Ladens).
 */
export function verlinkbar(lage: SperrLage | undefined): boolean {
  return lage !== undefined && lage.geladen && !lage.unvollstaendig && lage.treffer.length === 0;
}

/** Der Wert, als Link wenn erlaubt, und dahinter Kopieren (Muster der Kopfkarte). */
function WegWert({
  wert,
  email,
  lage,
  kopieren,
}: {
  readonly wert: string;
  readonly email: boolean;
  readonly lage: SperrLage | undefined;
  readonly kopieren: Kopieren;
}): ReactElement {
  const own = useKontaktStyles();
  const text = mitUmbruchstellen(wert, email);
  return (
    <Body1 className={own.wert}>
      {verlinkbar(lage) ? <a href={email ? mailtoAdresse(wert) : telAdresse(wert)}>{text}</a> : text}
      {/* Der Wortverbinder hält den Knopf am letzten Wortstück; er steht nie allein in einer Zeile. */}
      <span aria-hidden="true">{'\u2060'}</span>
      <Tooltip content="Kopieren" relationship="description">
        <Button
          appearance="subtle"
          size="small"
          className={own.kopieren}
          aria-label={`${email ? 'E-Mail' : 'Telefonnummer'} kopieren: ${wert}`}
          icon={<Symbol name="kopieren" groesse="1rem" />}
          onClick={() => kopieren(wert, email ? 'E-Mail-Adresse' : 'Telefonnummer')}
        />
      </Tooltip>
    </Body1>
  );
}

/**
 * Ein Kontaktweg.
 *
 * Der WERT steht vorn und gross, die Merkmale klein darunter. Umgekehrt waere
 * es eine Datenbanksicht: wer anruft, sucht die Nummer und nicht ihren
 * Verifikationsstatus. Der Status steht trotzdem da -- CM-PTY-005 verlangt,
 * dass Verwendung, Praeferenz und Bestaetigung sichtbar bleiben.
 */
function Kontaktweg({
  gruppe,
  label,
  gesperrt,
  kopieren,
}: {
  readonly gruppe: ContactPointGroup<ContactPointView>;
  readonly label: string;
  readonly kopieren: Kopieren;
  /** Die Sperren, die diesen Weg treffen (ST-CAM-001 C1a); jede Zeile desselben Wegs zeigt sie. */
  readonly gesperrt?: SperrenFuer | undefined;
}): ReactElement {
  const own = useKontaktStyles();
  const zwecke = gruppe.usages.map((usage) => usageLabel(usage.usageCode));
  // Eine Zeile der Feldliste (v4 field): Art links, Wert und Merkmale rechts.
  return (
    <div className={own.feld}>
      <dt className={own.feldName}>{label}</dt>
      <dd className={own.feldWert}>
      <WegWert wert={gruppe.value} email={label === 'E-Mail'} lage={gesperrt?.(label === 'E-Mail' ? 'email' : 'phone', gruppe.contactPointId)} kopieren={kopieren} />
      <span className={own.merkmale}>
        <Caption1>
          {zwecke.length === 1 ? zwecke[0] : `Zwecke: ${zwecke.join(', ')}`}
        </Caption1>
        {gruppe.usages.map((usage) =>
          usage.isPreferred ? (
            // In WORTEN und nicht als Stern: ein Symbol allein traegt die Aussage
            // nicht (NFR-ACC-001, WCAG 1.4.1). Bei mehreren Zwecken mit Zweck.
            <Badge key={usage.contactPointUsageId} appearance="outline" color="brand">
              {zwecke.length === 1 ? 'bevorzugt' : `bevorzugt: ${usageLabel(usage.usageCode)}`}
            </Badge>
          ) : undefined,
        )}
        {bestaetigungPilleSichtbar(gruppe.verificationStatus) ? (
          <Badge appearance="outline" color="informative">
            {verificationLabel(gruppe.verificationStatus)}
          </Badge>
        ) : undefined}
      </span>
      {gesperrt === undefined ? undefined : <SperrHinweis treffer={gesperrt(label === 'E-Mail' ? 'email' : 'phone', gruppe.contactPointId)} />}
      </dd>
    </div>
  );
}

/**
 * Die Website (Vorgabe DESIGN X2): eine eigene Zeile unter E-Mail und Telefon,
 * ein Link in einen neuen Tab, der das im Namen sagt. Keine Sperre und keine
 * Verwendung "Werbung": an eine Website wird nichts versandt. Kopiert wird die
 * gespeicherte Form ohne Protokoll, wie sie dasteht (DESIGN zu #774).
 */
function Website({ wert, kopieren }: { readonly wert: string; readonly kopieren: Kopieren }): ReactElement {
  const own = useKontaktStyles();
  const href = websiteHref(wert);
  return (
    <div className={own.feld}>
      <dt className={own.feldName}>Website</dt>
      <dd className={own.feldWert}>
        <Body1 className={own.wert}>
          {href === undefined ? (
            wert
          ) : (
            <a href={href} target="_blank" rel="noopener noreferrer" aria-label={`Website ${wert}, öffnet in neuem Tab`}>
              {textVorNeuerTab(wert).vorne}
              {/* Das letzte Stueck bleibt beim Zeichen; die Adresse davor bricht weiter um (B17). */}
              <span style={{ whiteSpace: 'nowrap' }}>
                {textVorNeuerTab(wert).hinten}
                <NeuerTabZeichen />
              </span>
            </a>
          )}
          <span aria-hidden="true">{'\u2060'}</span>
          <Tooltip content="Kopieren" relationship="description">
            <Button
              appearance="subtle"
              size="small"
              className={own.kopieren}
              aria-label={`Website kopieren: ${wert}`}
              icon={<Symbol name="kopieren" groesse="1rem" />}
              onClick={() => kopieren(wert, 'Website')}
            />
          </Tooltip>
        </Body1>
      </dd>
    </div>
  );
}

/**
 * Ein von der Organisation übernommener Kontaktweg: Wert, darunter "<Verwendung> · <Organisation>" (V3a′); keine Merkmale der Organisation.
 * Nur eine kanalweite Sperre der Person trifft ihn; eine Sperre auf einen eigenen Weg gilt nicht für den Weg der Organisation (decisions ST-CAM-001).
 */
function UebernommenerKontaktweg({
  wert,
  ueber,
  label,
  gesperrt,
  kopieren,
}: {
  readonly wert: ViaOrganizationValue;
  readonly ueber: ViaOrganization;
  readonly label: string;
  readonly gesperrt?: SperrenFuer | undefined;
  readonly kopieren: Kopieren;
}): ReactElement {
  const own = useKontaktStyles();
  return (
    <div className={own.feld}>
      <dt className={own.feldName}>
        <UebernommenerFeldname label={label} />
      </dt>
      <dd className={own.feldWert}>
        <WegWert wert={wert.value} email={label === 'E-Mail'} lage={gesperrt?.(label === 'E-Mail' ? 'email' : 'phone')} kopieren={kopieren} />
        <HerkunftCaption ueber={ueber} verwendung={wert.usage} />
        {gesperrt === undefined ? undefined : <SperrHinweis treffer={gesperrt(label === 'E-Mail' ? 'email' : 'phone')} />}
      </dd>
    </div>
  );
}

/**
 * `aktion`: der Weg zur Pflegeseite am Ende der Ueberschriftenzeile (v4 h2 .end).
 * `ueber`: was die Person von ihrer Organisation übernimmt (P7 A); je Art nach den eigenen Werten.
 */
export function Kontaktwege({
  party,
  aktion,
  ueber,
  gesperrt,
}: {
  readonly party: PartyDetail;
  readonly aktion?: ReactNode;
  readonly ueber?: ViaOrganization | undefined;
  /** Die Sperren, die einen Weg treffen (ST-CAM-001 C1a). */
  readonly gesperrt?: SperrenFuer | undefined;
}): ReactElement {
  const own = useKontaktStyles();
  const hinweis = withheldChannelNotice(party.otherContactPointCount);
  const zuviel = additionalContactPointNotice(party.additionalContactPointCount);
  const leer =
    party.emailAddresses.length === 0 &&
    party.phoneNumbers.length === 0 &&
    party.websites.length === 0 &&
    ueber?.emailAddress === undefined &&
    ueber?.phoneNumber === undefined;
  const [ansage, setAnsage] = useState('');
  const ansageLauf = useRef(0);
  // Erst leeren, dann setzen: dieselbe Meldung zweimal wird sonst nicht erneut
  // vorgelesen (WCAG 4.1.3; Muster der Kopfkarte). Die Zwischenablage kann fehlen
  // (unsicherer Kontext, verweigert): dann sagt der Abschnitt es (NFR-UX-005).
  const kopieren: Kopieren = (wert, was) => {
    const lauf = ++ansageLauf.current;
    setAnsage('');
    void Promise.resolve()
      .then(() => navigator.clipboard.writeText(wert))
      .then(
        () => `${was} kopiert.`,
        () => `${was} ließ sich nicht kopieren. Markiere den Text und kopiere ihn von Hand.`,
      )
      .then((satz) => {
        window.setTimeout(() => {
          if (lauf === ansageLauf.current) setAnsage(satz);
        }, 50);
      });
  };

  return (
    <section className={own.gruppe} aria-labelledby="kontaktwege">
      <div className={own.abschnittKopf} data-abschnittkopf="">
        <Subtitle1 as="h2" id="kontaktwege">
          Kontaktwege
        </Subtitle1>
        {aktion}
      </div>
      {leer ? (
        // Eine ehrliche Leerstelle: der Entwurf hat keinen Kontaktweg, und die
        // Anwendung sagt das, statt eine leere Liste ohne Erklaerung zu zeigen.
        <Body1 className={own.hinweis}>
          Für diesen Kontakt ist bisher kein Kontaktweg hinterlegt, den du sehen darfst.
        </Body1>
      ) : (
        <dl className={own.feldliste}>
          {groupContactPoints(party.emailAddresses).map((gruppe) => (
            <Kontaktweg key={gruppe.contactPointId} gruppe={gruppe} label="E-Mail" gesperrt={gesperrt} kopieren={kopieren} />
          ))}
          {ueber?.emailAddress === undefined ? undefined : <UebernommenerKontaktweg wert={ueber.emailAddress} ueber={ueber} label="E-Mail" gesperrt={gesperrt} kopieren={kopieren} />}
          {groupContactPoints(party.phoneNumbers).map((gruppe) => (
            <Kontaktweg key={gruppe.contactPointId} gruppe={gruppe} label="Telefon" gesperrt={gesperrt} kopieren={kopieren} />
          ))}
          {ueber?.phoneNumber === undefined ? undefined : <UebernommenerKontaktweg wert={ueber.phoneNumber} ueber={ueber} label="Telefon" gesperrt={gesperrt} kopieren={kopieren} />}
          {groupContactPoints(party.websites).map((gruppe) => (
            <Website key={gruppe.contactPointId} wert={gruppe.value} kopieren={kopieren} />
          ))}
        </dl>
      )}
      {hinweis === undefined ? undefined : <Caption1 className={own.hinweis}>{hinweis}</Caption1>}
      {zuviel === undefined ? undefined : <Caption1 className={own.hinweis}>{zuviel}</Caption1>}
      {/* Am Ende und aus dem Fluss: eine Statusregion vorn höbe die Kartenregel für das erste Element auf. */}
      <span role="status" aria-live="polite" aria-label="Status: Kontaktwege" className={own.nurVorgelesen}>
        {ansage}
      </span>
    </section>
  );
}
